import { useCallback } from 'react';
import { useUpdateTodoMutation } from 'hooks/business/chat/useTicketMutations';
import type { UseTicketListParams } from 'hooks/business/chat/useTicketMutations';
import { getTradeDefaultItem, getReturnSnList } from '../utils';
import { PureRelatedOrders } from './PureRelatedOrders';
import { useExtraFields } from 'views/Conversation/Sider/ToDoBundle/useExtraFields';
import { useTranslate } from '@tolgee/react';
import { CopyText } from '../../CopyText';

interface RelatedOrdersProps {
  ticket: Todo.TodoInstance;
  listParams: UseTicketListParams | null;
  readonly?: boolean;
}

const InfoItem = (props: { label: string; value?: string; copyable?: boolean; color?: string }) => {
  const { label, value, copyable = false, color } = props;

  if (!value) return null;

  return (
    <div style={{ color }}>
      {label}: {copyable ? <CopyText text={value} /> : value}
    </div>
  );
};

const ExtraInfo = (props: { ticket: Todo.TodoInstance }) => {
  const { ticket } = props;
  const { unit, hasContent, compensation, type8, type9, type10, type11 } = useExtraFields(ticket);
  const { t } = useTranslate();

  if (!hasContent) return null;

  return (
    <div className="bg-[#F7F9FA] rounded p-3 mb-3 text-[#232426]">
      {compensation.visible && (
        <InfoItem label={t('赔偿金额')} value={`${unit} ${compensation.amount}`} />
      )}
      {type8.visible && (
        <>
          <InfoItem label={t('trade.订单编号')} value={type8.relatedTradeId} copyable />
          <InfoItem label={t('物流单号')} value={type8.relatedTraceNum} copyable />
          <InfoItem label={t('卖家支付运费金额')} value={`${unit} ${type8.freightAmount}`} />
        </>
      )}
      {type9.visible && (
        <>
          <InfoItem label={t('trade.订单编号')} value={type9.relatedTradeId} copyable />
          <InfoItem label={t('Entities.下单时间')} value={type9.orderTimeFormatted} />
          <InfoItem label={t('等待发货时长')} value={type9.stillTime} color="#F05B5B" />
        </>
      )}
      {type10.visible && (
        <>
          <InfoItem label={t('trade.订单编号')} value={type10.relatedTradeId} copyable />
          <InfoItem label={t('物流单号')} value={type10.relatedTraceNum} copyable />
          <InfoItem label={t('Entities.下单时间')} value={type10.orderTimeFormatted} />
          <InfoItem label={t('等待揽收时长')} value={type10.stillTime} color="#F05B5B" />
        </>
      )}
      {type11.visible && (
        <>
          <InfoItem label={t('trade.订单编号')} value={type11.relatedTradeId} copyable />
          <InfoItem label={t('物流单号')} value={type11.relatedTraceNum} copyable />
          <InfoItem label={t('发货时间')} value={type11.deliveryTimeFormatted} />
          <InfoItem label={t('运输时长')} value={type11.stillTime} color="#F05B5B" />
        </>
      )}
    </div>
  );
};

export const RelatedOrders = (props: RelatedOrdersProps) => {
  const { ticket, listParams, readonly = false } = props;
  const { id, relatedTradeId, platform, storeId } = ticket;

  const externalBuyerId = listParams?.tradeBuyerId ?? '';
  const ready = Boolean(listParams && platform !== 'FACEBOOK');

  const updateTodoMutation = useUpdateTodoMutation(listParams);

  const handleSelectOrder = useCallback(
    (trade: Conversation.Trade.Vo | null) => {
      if (!trade) {
        updateTodoMutation.mutate({
          id,
          relatedTradeId: '',
          relatedSku: [],
          relatedReturnId: '',
        });
        return;
      }
      const relatedReturnId = getReturnSnList(trade)?.[0] ?? '';
      updateTodoMutation.mutate({
        id,
        relatedTradeId: trade.externalTradeId,
        relatedSku: getTradeDefaultItem(trade),
        relatedReturnId,
      });
    },
    [id, updateTodoMutation],
  );

  const handleClearOrder = useCallback(() => {
    if (!relatedTradeId) return;
    updateTodoMutation.mutate({
      id,
      relatedTradeId: '',
      relatedSku: [],
      relatedReturnId: '',
    });
  }, [id, relatedTradeId, updateTodoMutation]);

  const canEdit = Boolean(listParams && !readonly);

  if ((!canEdit && !relatedTradeId) || !ready) {
    return null;
  }

  return (
    <>
      <PureRelatedOrders
        canEdit={canEdit}
        relatedTradeId={relatedTradeId}
        platform={platform}
        externalBuyerId={externalBuyerId}
        storeId={storeId}
        onSelect={handleSelectOrder}
        onClear={handleClearOrder}
      />
      <ExtraInfo ticket={ticket} />
    </>
  );
};
